import { z } from "zod";
import { API_PREFIX, timestampSchema, uuidSchema } from "./api.ts";
import { anyEntityTypeSchema, isBusinessEntityType, type AnyEntityType } from "./common.ts";
import { notificationSchema } from "./notifications.ts";

/**
 * Realtime — Server-Sent Events at GET /api/v1/stream
 *
 * - Auth: session cookie (same-origin EventSource sends it) or API key. No CSRF
 *   (GET). One stream per browser tab; the server fans out per org from the
 *   transactional outbox (at-least-once — events may repeat; consumers are
 *   idempotent because an event only triggers a refetch).
 * - Wire format: default "message" events (NO `event:` line); `data:` is one
 *   line of JSON matching `realtimeEventSchema` (discriminated on `type`);
 *   `id:` is the outbox event id (uuid v7). The server sends `retry: 3000` first
 *   and a `: ping` comment every 25 s to keep proxies from closing the stream.
 * - Resume: on reconnect the browser sends `Last-Event-ID`; the server replays
 *   what it still has, otherwise sends `{type:"resync"}` → the web invalidates
 *   every query.
 * - Filtering: only events for the caller's org, for entity types they can read
 *   (ENTITY_READ_PERMISSION); `notification` events only to their recipient.
 *
 * QUERY-KEY CONVENTION (TanStack Query, web):
 *   every key starts with the entityType string —
 *     list:      [entityType, "list", params]      e.g. ["client", "list", { q: "acme" }]
 *     detail:    [entityType, id]                   e.g. ["client", "0192…"]
 *     sub-data:  [entityType, id, "<sub>", params?] e.g. ["project", id, "tasks"]
 *   plus a few non-entity roots: ["activity", entityType, id], ["audit", params],
 *   ["search", params], ["me"], ["report", …], ["dashboard", …].
 *   An `invalidate` event for entityType "client" invalidates the prefix
 *   ["client"] (all client lists + details) — see `invalidationKeysFor`.
 *   Use `queryKeys` below instead of hand-writing keys.
 */

export const STREAM_PATH = `${API_PREFIX}/stream`;
export const REALTIME_HEARTBEAT_MS = 25_000;
export const REALTIME_RETRY_MS = 3_000;

/** A record (or collection) changed; refetch anything derived from it. */
export const invalidateEventSchema = z.object({
  type: z.literal("invalidate"),
  entityType: anyEntityTypeSchema,
  /** Omitted/null = collection-level change (bulk import, reorder…). */
  entityId: uuidSchema.nullable().optional(),
  /** Audit-style action, e.g. "client.updated". */
  action: z.string().max(80),
  /** Owning record for child changes (comment on a task, contact of a client). */
  parent: z.object({ entityType: anyEntityTypeSchema, entityId: uuidSchema }).nullable().optional(),
  /** Who caused it (lets the web skip "updated by someone else" toasts for own edits). */
  actorId: uuidSchema.nullable().optional(),
});
export type InvalidateEvent = z.infer<typeof invalidateEventSchema>;

/** A new notification for the current user. */
export const notificationEventSchema = z.object({
  type: z.literal("notification"),
  notification: notificationSchema,
  /** Badge count after this notification (omit → web refetches the count). */
  unreadCount: z.number().int().nonnegative().optional(),
});
export type NotificationEvent = z.infer<typeof notificationEventSchema>;

/** First event on every (re)connection. */
export const readyEventSchema = z.object({
  type: z.literal("ready"),
  connectionId: z.string(),
  serverTime: timestampSchema,
});
export type ReadyEvent = z.infer<typeof readyEventSchema>;

/** The server could not replay missed events: invalidate everything. */
export const resyncEventSchema = z.object({
  type: z.literal("resync"),
  reason: z.string().max(200),
});
export type ResyncEvent = z.infer<typeof resyncEventSchema>;

export const realtimeEventSchema = z.discriminatedUnion("type", [invalidateEventSchema, notificationEventSchema, readyEventSchema, resyncEventSchema]);
export type RealtimeEvent = z.infer<typeof realtimeEventSchema>;

// ---------------------------------------------------------------------------
// Query keys (web) — the single source of truth for key shapes
// ---------------------------------------------------------------------------

export type QueryKey = readonly unknown[];

export const queryKeys = {
  /** Prefix for everything about a type. */
  all: (entityType: AnyEntityType) => [entityType] as const,
  list: (entityType: AnyEntityType, params: Record<string, unknown> = {}) => [entityType, "list", params] as const,
  detail: (entityType: AnyEntityType, id: string) => [entityType, id] as const,
  sub: (entityType: AnyEntityType, id: string, sub: string, params?: Record<string, unknown>) =>
    (params ? [entityType, id, sub, params] : [entityType, id, sub]) as QueryKey,
  activity: (entityType: AnyEntityType, id: string) => ["activity", entityType, id] as const,
  audit: (params: Record<string, unknown> = {}) => ["audit", params] as const,
  search: (params: Record<string, unknown>) => ["search", params] as const,
  me: () => ["me"] as const,
  org: () => ["org"] as const,
  notificationsUnreadCount: () => ["notification", "unread-count"] as const,
  notificationPreferences: () => ["notification", "preferences"] as const,
} as const;

/** Types whose change can alter the current user's session/permissions. */
const ME_AFFECTING: readonly AnyEntityType[] = ["org", "member", "role", "user"];

/**
 * Pure: query-key prefixes to invalidate for an event. Use with
 * `queryClient.invalidateQueries({ queryKey, refetchType: "active" })`.
 */
export function invalidationKeysFor(event: RealtimeEvent): QueryKey[] {
  switch (event.type) {
    case "invalidate": {
      const keys: QueryKey[] = [[event.entityType]];
      if (event.entityId) keys.push(["activity", event.entityType, event.entityId]);
      if (event.parent) {
        keys.push([event.parent.entityType, event.parent.entityId]);
        keys.push(["activity", event.parent.entityType, event.parent.entityId]);
      }
      if (ME_AFFECTING.includes(event.entityType)) keys.push(["me"]);
      if (isBusinessEntityType(event.entityType)) keys.push(["search"], ["report"], ["dashboard"]);
      keys.push(["audit"]);
      return keys;
    }
    case "notification":
      return event.unreadCount === undefined ? [["notification", "list"], ["notification", "unread-count"]] : [["notification", "list"]];
    case "resync":
      return [[]];
    case "ready":
      return [];
  }
}

// ---------------------------------------------------------------------------
// SSE framing helpers (server) and parsing (web)
// ---------------------------------------------------------------------------

/** Frames one event: `id: <id>` (optional) + a single-line `data:` + blank line. */
export function formatSseEvent(event: RealtimeEvent, id?: string): string {
  return `${id ? `id: ${id}\n` : ""}data: ${JSON.stringify(event)}\n\n`;
}
/** Keep-alive comment frame. */
export function formatSseComment(text = "ping"): string {
  return `: ${text.replace(/[\r\n]+/g, " ")}\n\n`;
}
/** Reconnection delay frame (send once at stream start). */
export function formatSseRetry(ms = REALTIME_RETRY_MS): string {
  return `retry: ${Math.max(0, Math.floor(ms))}\n\n`;
}

/** Web: parse `MessageEvent.data`; returns null for malformed/unknown events (forward compatible). */
export function parseRealtimeEvent(data: string): RealtimeEvent | null {
  try {
    const parsed = realtimeEventSchema.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
